/**
 * ボタン直下に開く小さなポップオーバー (フォント一覧・位置グリッド・色の選択など) の共通シェル。
 *
 * 右ドックの中は要素が画面右端に近く、かつドック本体が縦スクロールする領域 (overflow: auto)
 * なので、親要素基準の absolute 配置ではメニューが画面右端やドックの端で切れてしまう。
 * 共通の Tooltip と同じく body 直下へポータルして fixed で配置し、実寸を測ってビューポート内
 * (左右 8px の余白) に収める。下に入りきらなければアンカーの上側に出す。
 *
 * 外側クリック / Esc で onClose を呼ぶ (ポータル先の要素とアンカーの内側は対象外)。
 * closeOnOutsideClick=false の間は外側クリックで閉じない (中で名前を入力中など、誤クリックで
 * 入力を失いたくないとき)。Esc は変わらず onClose を呼ぶ。
 *
 * align でアンカーとの横位置の揃え方を選ぶ。既定の 'left' はアンカーの左端に左端を合わせて
 * 右へ広がる。'right' は右端どうしを合わせて左へ広がるので、画面右寄り (右ドックの中など) の
 * トリガーから幅のあるメニューを出しても、画面端で頭打ちにならず自然な位置に出る。
 *
 * placement='above' はアンカーの上側を優先する (入りきらなければ下)。inModal はモーダルの中に
 * 置いたトリガー用で、重なり順をモーダルより上 (--z-popover) にする。
 */
import { useEffect, useLayoutEffect, useRef, useState, type ReactNode, type RefObject } from 'react';
import { createPortal } from 'react-dom';
import { computePopoverPosition } from '../../../utils/popoverPosition';

interface PopoverProps {
  /** 位置の基準にする要素 (トリガーを含むラッパー)。 */
  anchorRef: RefObject<HTMLElement | null>;
  open: boolean;
  onClose: () => void;
  /** 外側クリックで onClose を呼ぶか (既定 true)。 */
  closeOnOutsideClick?: boolean;
  className?: string;
  /** アンカーとの横位置の揃え方。'left' = 左端を合わせて右へ広がる (既定) / 'right' = 右端を合わせて左へ広がる。 */
  align?: 'left' | 'right';
  /** アンカーの上下どちらを優先して開くか (既定 'below')。入りきらなければ反対側に出す。 */
  placement?: 'below' | 'above';
  /** モーダルの中から開く (重なり順をモーダルより上にする)。 */
  inModal?: boolean;
  role?: string;
  ariaLabel?: string;
  ariaMultiselectable?: boolean;
  children: ReactNode;
}

export function Popover({ anchorRef, open, onClose, closeOnOutsideClick = true, className, align = 'left', placement = 'below', inModal = false, role, ariaLabel, ariaMultiselectable, children }: PopoverProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  // 表示前に実寸を測って位置を決める。リサイズ・スクロール (ドック内を含む) で追従する。
  useLayoutEffect(() => {
    if (!open) {
      setPos(null);
      return;
    }
    const update = () => {
      const anchor = anchorRef.current;
      const el = ref.current;
      if (!anchor || !el) return;
      const a = anchor.getBoundingClientRect();
      setPos(
        computePopoverPosition({
          anchor: { top: a.top, bottom: a.bottom, left: a.left, right: a.right },
          size: { width: el.offsetWidth, height: el.offsetHeight },
          viewport: { width: window.innerWidth, height: window.innerHeight },
          align,
          placement,
        }),
      );
    };
    update();
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [open, anchorRef, align, placement]);

  useEffect(() => {
    if (!open) return;
    const handleMouseDown = (e: MouseEvent) => {
      if (!closeOnOutsideClick) return;
      const target = e.target as Node;
      if (ref.current?.contains(target) || anchorRef.current?.contains(target)) return;
      // 中のヘルプ (HelpTooltip) の吹き出しは body へポータル描画されるので、外側クリックとして扱わない
      if (target instanceof Element && target.closest('[role="tooltip"]')) return;
      onClose();
    };
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('mousedown', handleMouseDown);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('keydown', handleKey);
    };
  }, [open, onClose, anchorRef, closeOnOutsideClick]);

  if (!open) return null;

  return createPortal(
    <div
      ref={ref}
      className={`modal-surface ${className ?? ''}`}
      role={role}
      aria-label={ariaLabel}
      aria-multiselectable={ariaMultiselectable}
      style={{
        position: 'fixed',
        top: pos?.top ?? -9999,
        left: pos?.left ?? -9999,
        zIndex: inModal ? 'var(--z-popover)' : 'var(--z-dropdown)',
      }}
    >
      {children}
    </div>,
    document.body,
  );
}
